import { useState } from 'react';
import type { FormEvent } from 'react';
import { Form } from 'react-aria-components';
import { Trans, useTranslation } from 'react-i18next';

import { Button, ButtonWithLoading } from '@actual-app/components/button';
import { FormError } from '@actual-app/components/form-error';
import { Input } from '@actual-app/components/input';
import { Select } from '@actual-app/components/select';
import { Text } from '@actual-app/components/text';
import { theme } from '@actual-app/components/theme';
import { View } from '@actual-app/components/view';
import { send } from '@actual-app/core/platform/client/connection';
import { DEFAULT_BACKFILL_MAX_GAP_DAYS } from '@actual-app/core/server/exchange-rates/app';
import { currentDay } from '@actual-app/core/shared/months';
import type { ExchangeRateEntity } from '@actual-app/core/types/models';

import { Checkbox } from '#components/forms';
import {
  useCreateExchangeRateMutation,
  useDeleteExchangeRateMutation,
  useFetchExchangeRatesMutation,
} from '#exchange-rates';
import { useCurrencyOptions } from '#hooks/useCurrencyOptions';
import { useExchangeRates } from '#hooks/useExchangeRates';
import { useOpenExchangeRatesStatus } from '#hooks/useOpenExchangeRatesStatus';
import { useSyncedPref } from '#hooks/useSyncedPref';
import { useSyncServerStatus } from '#hooks/useSyncServerStatus';
import { pushModal } from '#modals/modalsSlice';
import { useDispatch } from '#redux';

import { Setting } from './UI';

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

function RateRow({
  rate,
  onDelete,
}: {
  rate: ExchangeRateEntity;
  onDelete: () => void;
}) {
  const { t } = useTranslation();
  return (
    <View
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
        padding: '4px 0',
        borderBottom: `1px solid ${theme.tableBorder}`,
      }}
      data-testid="exchange-rate-row"
    >
      <Text style={{ width: 95 }}>{rate.date}</Text>
      <Text style={{ width: 110 }}>
        1 {rate.source_currency} = {rate.rate} {rate.target_currency}
      </Text>
      <Text style={{ flex: 1, color: theme.pageTextSubdued }}>
        {rate.source ?? ''}
      </Text>
      <Button variant="bare" onPress={onDelete} aria-label={t('Delete rate')}>
        <Trans>Delete</Trans>
      </Button>
    </View>
  );
}

/**
 * Open Exchange Rates set-up: the App ID lives on the sync server (like a
 * bank-sync token); the daily pull runs in the budget worker after sync.
 */
function ExchangeRateProviderSettings() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const syncServerStatus = useSyncServerStatus();
  const { configured, isLoading, setConfigured } = useOpenExchangeRatesStatus();
  const [autoFetchPref, setAutoFetchPref] = useSyncedPref(
    'exchangeRatesAutoFetch',
  );
  const autoFetch = autoFetchPref !== 'false';
  const [backfillPref, setBackfillPref] = useSyncedPref(
    'exchangeRatesBackfill',
  );
  const backfill = backfillPref !== 'false';
  const [backfillGapPref, setBackfillGapPref] = useSyncedPref(
    'exchangeRatesBackfillMaxGapDays',
  );
  const [backfillGapDraft, setBackfillGapDraft] = useState(
    backfillGapPref ?? String(DEFAULT_BACKFILL_MAX_GAP_DAYS),
  );
  const commitBackfillGap = (value: string) => {
    const parsed = parseInt(value.trim(), 10);
    const days =
      Number.isFinite(parsed) && parsed >= 0
        ? parsed
        : DEFAULT_BACKFILL_MAX_GAP_DAYS;
    setBackfillGapDraft(String(days));
    setBackfillGapPref(String(days));
  };

  const fetchRates = useFetchExchangeRatesMutation();
  const [fetchDate, setFetchDate] = useState('');
  const [summary, setSummary] = useState<string | null>(null);
  const [isResetting, setIsResetting] = useState(false);

  const onConfigure = () => {
    dispatch(
      pushModal({
        modal: {
          name: 'openexchangerates-init',
          options: {
            onSuccess: () => {
              setConfigured(true);
              void runFetch();
            },
          },
        },
      }),
    );
  };

  const onReset = async () => {
    setIsResetting(true);
    await send('secret-set', { name: 'openexchangerates_appId', value: null });
    setConfigured(false);
    setSummary(null);
    setIsResetting(false);
  };

  const runFetch = async (date?: string) => {
    if (date && !ISO_DATE.test(date)) {
      setSummary(t('Date must be in the format YYYY-MM-DD'));
      return;
    }
    const result = await fetchRates.mutateAsync(date ? { date } : {});
    if (result.ok === false) {
      setSummary(null);
      return;
    }
    const parts = [];
    if (result.currencies.length > 0) {
      parts.push(
        t('Stored {{date}} rates for {{currencies}}.', {
          date: result.date,
          currencies: result.currencies.join(', '),
        }),
      );
    }
    if (result.skipped.length > 0) {
      parts.push(
        t('Kept your manual {{date}} rates for {{currencies}}.', {
          date: result.date,
          currencies: result.skipped.join(', '),
        }),
      );
    }
    if (parts.length === 0) {
      parts.push(
        t('The provider returned no rates for {{date}}.', {
          date: result.date,
        }),
      );
    }
    setSummary(parts.join(' '));
  };

  const statusLabel =
    syncServerStatus !== 'online'
      ? t('Needs a connection to your Actual server')
      : isLoading || configured == null
        ? t('Checking…')
        : configured
          ? t('Configured')
          : t('Not configured');

  return (
    <View
      style={{
        alignSelf: 'stretch',
        border: `1px solid ${theme.tableBorder}`,
        borderRadius: 8,
        padding: 12,
        backgroundColor: theme.tableBackground,
        gap: 10,
      }}
      data-testid="exchange-rate-provider-openexchangerates"
    >
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 10,
        }}
      >
        <View style={{ flex: 1, minWidth: 200, gap: 2 }}>
          <Text style={{ fontSize: 15, fontWeight: 600 }}>
            <Trans>Open Exchange Rates</Trans>
          </Text>
          <Text
            style={{
              fontSize: 13,
              fontWeight: 500,
              color: configured ? theme.noticeTextDark : theme.pageTextSubdued,
            }}
          >
            {statusLabel}
          </Text>
        </View>
        {syncServerStatus === 'online' &&
          (configured ? (
            <>
              <ButtonWithLoading
                variant="primary"
                isLoading={fetchRates.isPending}
                onPress={() => void runFetch()}
              >
                <Trans>Fetch the latest rates</Trans>
              </ButtonWithLoading>
              <Button onPress={onConfigure}>
                <Trans>Change App ID</Trans>
              </Button>
              <ButtonWithLoading
                variant="bare"
                isLoading={isResetting}
                onPress={() => void onReset()}
              >
                <Trans>Remove</Trans>
              </ButtonWithLoading>
            </>
          ) : (
            <Button
              variant="primary"
              isDisabled={isLoading || configured == null}
              onPress={onConfigure}
            >
              <Trans>Set up</Trans>
            </Button>
          ))}
      </View>

      {configured && (
        <>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
            <Checkbox
              id="settings-exchangeRatesAutoFetch"
              checked={autoFetch}
              onChange={e =>
                setAutoFetchPref(e.target.checked ? 'true' : 'false')
              }
            />
            <label htmlFor="settings-exchangeRatesAutoFetch">
              <Trans>
                Fetch the latest rates automatically once a day when this budget
                is open
              </Trans>
            </label>
          </View>

          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 6,
            }}
          >
            <Checkbox
              id="settings-exchangeRatesBackfill"
              checked={backfill}
              onChange={e =>
                setBackfillPref(e.target.checked ? 'true' : 'false')
              }
            />
            <label htmlFor="settings-exchangeRatesBackfill">
              <Trans>
                Fetch a historical rate when a transaction is more than
              </Trans>
            </label>
            <Input
              aria-label={t('Days from the nearest stored rate')}
              value={backfillGapDraft}
              onChangeValue={setBackfillGapDraft}
              onUpdate={commitBackfillGap}
              onEnter={commitBackfillGap}
              inputMode="numeric"
              disabled={!backfill}
              style={{ width: 50 }}
            />
            <label htmlFor="settings-exchangeRatesBackfill">
              <Trans>days from the nearest stored rate</Trans>
            </label>
          </View>

          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 10,
            }}
          >
            <Text style={{ color: theme.pageTextSubdued }}>
              <Trans>Or fetch the rates for a past day:</Trans>
            </Text>
            <Input
              value={fetchDate}
              onChangeValue={setFetchDate}
              placeholder="YYYY-MM-DD"
              style={{ width: 120 }}
            />
            <ButtonWithLoading
              isLoading={fetchRates.isPending}
              isDisabled={!fetchDate}
              onPress={() => void runFetch(fetchDate)}
            >
              <Trans>Fetch for date</Trans>
            </ButtonWithLoading>
          </View>
        </>
      )}

      {summary && (
        <Text style={{ color: theme.pageTextSubdued }}>{summary}</Text>
      )}
    </View>
  );
}

export function ExchangeRateSettings() {
  const { t } = useTranslation();
  const [defaultCurrencyCode] = useSyncedPref('defaultCurrencyCode');
  const budgetCurrency = defaultCurrencyCode || '';
  const currencyOptions = useCurrencyOptions().filter(([code]) => code !== '');

  const { data: rates = [] } = useExchangeRates();
  const createRate = useCreateExchangeRateMutation();
  const deleteRate = useDeleteExchangeRateMutation();

  const [date, setDate] = useState(currentDay());
  const [source, setSource] = useState('');
  const [target, setTarget] = useState(budgetCurrency);
  const [rate, setRate] = useState('');
  const [error, setError] = useState<string | null>(null);

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const parsed = parseFloat(rate);
    if (!ISO_DATE.test(date)) {
      setError(t('Date must be in the format YYYY-MM-DD'));
      return;
    }
    if (!source || !target) {
      setError(t('Choose both currencies'));
      return;
    }
    if (source === target) {
      setError(t('Source and target currency must differ'));
      return;
    }
    if (!Number.isFinite(parsed) || parsed <= 0) {
      setError(t('Rate must be a positive number'));
      return;
    }
    setError(null);
    createRate.mutate(
      {
        date,
        source_currency: source,
        target_currency: target,
        rate: parsed,
        source: 'manual',
      },
      { onSuccess: () => setRate('') },
    );
  };

  return (
    <Setting>
      <Text>
        <strong>
          <Trans>Exchange rates</Trans>
        </strong>{' '}
        <Trans>
          Rates convert foreign-currency accounts into the budget currency. Each
          transaction uses the rate dated closest to it; account balances use
          the most recent rate. Native account balances are never affected.
        </Trans>
      </Text>

      <ExchangeRateProviderSettings />

      <Text style={{ fontWeight: 500 }}>
        <Trans>Enter a rate by hand</Trans>
      </Text>

      <Form onSubmit={onSubmit}>
        <View
          style={{
            flexDirection: 'row',
            flexWrap: 'wrap',
            alignItems: 'flex-end',
            gap: 10,
          }}
        >
          <View style={{ gap: 3 }}>
            <Text style={{ fontWeight: 500 }}>
              <Trans>Date</Trans>
            </Text>
            <Input
              value={date}
              onChangeValue={setDate}
              placeholder="YYYY-MM-DD"
              style={{ width: 120 }}
            />
          </View>
          <View style={{ gap: 3 }}>
            <Text style={{ fontWeight: 500 }}>
              <Trans>1 unit of</Trans>
            </Text>
            <Select
              value={source}
              onChange={setSource}
              options={currencyOptions}
              defaultLabel={t('Choose a currency')}
              style={{ width: 200 }}
            />
          </View>
          <View style={{ gap: 3 }}>
            <Text style={{ fontWeight: 500 }}>
              <Trans>equals</Trans>
            </Text>
            <Input
              value={rate}
              onChangeValue={setRate}
              inputMode="decimal"
              placeholder="1.0000"
              style={{ width: 110 }}
            />
          </View>
          <View style={{ gap: 3 }}>
            <Text style={{ fontWeight: 500 }}>
              <Trans>of</Trans>
            </Text>
            <Select
              value={target}
              onChange={setTarget}
              options={currencyOptions}
              defaultLabel={t('Choose a currency')}
              style={{ width: 200 }}
            />
          </View>
          <Button
            type="submit"
            variant="primary"
            isDisabled={createRate.isPending}
          >
            <Trans>Add rate</Trans>
          </Button>
        </View>
        {error && <FormError style={{ marginTop: 5 }}>{error}</FormError>}
      </Form>

      {rates.length === 0 ? (
        <Text style={{ color: theme.pageTextSubdued }}>
          <Trans>No exchange rates stored yet.</Trans>
        </Text>
      ) : (
        <View>
          {rates.map(r => (
            <RateRow
              key={r.id}
              rate={r}
              onDelete={() => deleteRate.mutate({ id: r.id })}
            />
          ))}
        </View>
      )}
    </Setting>
  );
}
